import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { motion } from 'motion/react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LegalDocumentRenderer from '../components/LegalDocumentRenderer';

import {
  UsersThree,
  WarningCircle
} from '@phosphor-icons/react';
const AffiliateTerms = () => {
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDocument();
  }, []);

  const fetchDocument = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/legal/affiliate_terms`
      );

      if (response.ok) {
        const data = await response.json();
        setDocument(data);
      } else if (response.status === 404) {
        setError('Affiliate Terms have not been published yet.');
      } else {
        setError('Failed to load Affiliate Terms.');
      }
    } catch (err) {
      console.error('Error fetching affiliate terms:', err);
      setError('Failed to load Affiliate Terms.');
    } finally {
      setLoading(false);
    }
  };

  const springConfig = {
    type: 'spring',
    damping: 80,
    stiffness: 400,
    mass: 1,
  };

  return (
    <div className="bg-[#161B26]">
      <SEO 
        title="Affiliate Terms - Man & Machine"
        description="Read our affiliate program terms and conditions."
        canonicalUrl="https://manandmachine.app/affiliate-terms"
      />
      <Header />

      {/* Hero Section - Compact */}
      <section className="pt-24 pb-12 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#EC4899] rounded-full filter blur-3xl opacity-20"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-[#6366F1]/20 to-[#EC4899]/20 mb-4">
              <UsersThree size={32} className="text-[#EC4899]" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Affiliate Terms
            </h1>
            {document?.updated_at && (
              <p className="text-sm text-[#A0AEC0]">
                Last updated: {new Date(document.updated_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 pb-24">
        <div className="max-w-4xl mx-auto px-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6366F1] mx-auto"></div>
              <p className="text-[#A0AEC0] mt-4">Loading...</p>
            </div>
          ) : error ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springConfig}
              className="text-center py-12"
            >
              <div className="inline-flex p-4 rounded-full bg-yellow-500/10 mb-4">
                <WarningCircle size={32} className="text-yellow-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Content Not Available</h2>
              <p className="text-[#A0AEC0]">{error}</p>
              <p className="text-[#A0AEC0] mt-2">Please check back later or contact us for more information.</p>
            </motion.div>
          ) : document?.content ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springConfig, delay: 0.2 }}
              className="prose prose-invert prose-lg max-w-none"
            >
              <LegalDocumentRenderer content={document.content} />
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springConfig}
              className="text-center py-12"
            >
              <div className="inline-flex p-4 rounded-full bg-gray-500/10 mb-4">
                <WarningCircle size={32} className="text-gray-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Coming Soon</h2>
              <p className="text-[#A0AEC0]">Affiliate Terms are being finalized and will be available shortly.</p>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AffiliateTerms;
